import { FC } from 'react';

import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';

import { SubHeading } from 'src/components';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import useCountryHelpers from 'src/hooks/useCountryHelpers';
import { ServiceChargeCalculatedAt } from 'src/types/AddMerchant/merchantInfo';
import findBillingContact from 'src/utils/findBillingContact';

import AddressDisplay from '../AddressDisplay';
import { CardSection } from '../CardSection';
import { CountryDisplay } from '../CountryDisplay';
import { TextDisplay } from '../TextDisplay';

const OutletInformation: FC = () => {
    const { merchantApplication } = useAddMerchantContext();
    const { getPostalLabel } = useCountryHelpers();
    const postalLabel = getPostalLabel(merchantApplication?.partner?.countryDetails?.alpha2Code);

    const numberOfOutlets = merchantApplication?.merchantInformation?.outlets.length;
    const billingContactDetails = findBillingContact(
        merchantApplication?.merchantInformation?.principals
    );
    return (
        <Box>
            {(merchantApplication?.merchantInformation?.outlets ?? []).map((outlet, index) => {
                return (
                    <CardSection heading={`Outlet ${index + 1}`} key={index}>
                        <Box display="flex" gap="24px" flexDirection="column">
                            <Box display="flex" alignItems="center">
                                <Typography
                                    fontSize="14px"
                                    color="#575757"
                                    fontWeight="400"
                                    marginRight="8px"
                                >
                                    Service charges Calculated At
                                </Typography>
                                <RadioGroup
                                    aria-labelledby="service-charge-level-radio-buttons"
                                    name="serviceChargeCalculatedAt"
                                    row
                                    value={
                                        outlet.bankingDetails?.serviceChargeCalculatedAt ||
                                        ServiceChargeCalculatedAt.GROUPLEVEL
                                    }
                                    color="inherit"
                                >
                                    <FormControlLabel
                                        value={ServiceChargeCalculatedAt.GROUPLEVEL}
                                        control={<Radio sx={{ color: '#F39E4E' }} />}
                                        label="Group Level"
                                        color="inherit"
                                    />
                                    <FormControlLabel
                                        value={ServiceChargeCalculatedAt.COMPANYLEVEL}
                                        control={<Radio sx={{ color: '#F39E4E' }} />}
                                        label="Company Level"
                                        color="inherit"
                                    />
                                    <FormControlLabel
                                        value={ServiceChargeCalculatedAt.OUTLETLEVEL}
                                        control={<Radio sx={{ color: '#F39E4E' }} />}
                                        label="Outlet Level"
                                        color="inherit"
                                    />
                                </RadioGroup>
                            </Box>
                            <TextDisplay label="Outlet Name" value={outlet?.name ?? ''} />
                            <AddressDisplay
                                heading="Trading Address"
                                postalLabel={postalLabel}
                                eirCode={outlet?.tradingAddress?.eircode}
                                addressLine1={outlet?.tradingAddress?.addressLine1}
                                addressLine2={outlet?.tradingAddress?.addressLine2}
                                city={outlet?.tradingAddress?.townOrCity}
                                country={outlet?.tradingAddress?.country}
                                county={outlet?.tradingAddress?.county}
                            />
                            <SubHeading subHeading="Site Contact" />
                            <Box width="100%" display="flex" justifyContent="space-between">
                                <TextDisplay
                                    label="First Name"
                                    value={
                                        numberOfOutlets === 1
                                            ? billingContactDetails.firstName ?? ''
                                            : outlet?.siteContact?.firstName ?? ''
                                    }
                                />
                                <TextDisplay
                                    label="Last Name"
                                    value={
                                        numberOfOutlets === 1
                                            ? billingContactDetails.lastName ?? ''
                                            : outlet?.siteContact?.lastName ?? ''
                                    }
                                />
                            </Box>
                            <Box width="100%" display="flex" justifyContent="space-between">
                                <TextDisplay
                                    label="Email Address"
                                    value={
                                        numberOfOutlets === 1
                                            ? billingContactDetails.email ?? ''
                                            : outlet?.siteContact?.email ?? ''
                                    }
                                />
                                <CountryDisplay
                                    label="Mobile Number"
                                    value={
                                        numberOfOutlets === 1
                                            ? billingContactDetails.mobileNumber ?? ''
                                            : outlet?.siteContact?.mobileNumber ?? ''
                                    }
                                    country={outlet?.tradingAddress?.country}
                                />
                            </Box>
                            <Box width="100%" display="flex" justifyContent="space-between">
                                <CountryDisplay
                                    label="Telephone Number"
                                    value={
                                        numberOfOutlets === 1
                                            ? billingContactDetails?.telephoneNumber ?? ''
                                            : outlet?.siteContact?.telephoneNumber ?? ''
                                    }
                                    country={outlet?.tradingAddress?.country}
                                />
                                <TextDisplay
                                    label="Position"
                                    value={
                                        numberOfOutlets === 1
                                            ? billingContactDetails?.position ?? ''
                                            : outlet?.siteContact?.position ?? ''
                                    }
                                />
                            </Box>
                        </Box>
                    </CardSection>
                );
            })}
        </Box>
    );
};

export default OutletInformation;
